import {
  AlertCircle,
  ClipboardList,
  MapPin,
  Plus,
  RotateCcw,
  Save,
  ScanLine,
  Sigma,
  X
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import Modal from '../../../../components/shared/Modal/Modal';
import { useToast } from '../../../../hooks/useToast';
import { findBookByCode, listBooks } from '../../../../services/BooksService';
import { listCompanies } from '../../../../services/CompaniesService';
import { createStockMovement, listWarehouseStock } from '../../../../services/StockMovementsService';
import { listWarehouses } from '../../../../services/WarehousesService';
import { getActiveCompanyId } from '../../../../services/apiUtils';
import { Book, Society, Warehouse } from '../../../../types';
import { formatQuantity, parseNumber } from '../PurchasesView/components/format';
import { ScreenSkeleton } from '../ScreenSkeleton';
import '../PurchasesView/PurchasesView.css';

type CodeField = 'ean' | 'sku' | 'isbn';

const CODE_LABELS: Record<CodeField, string> = {
  ean: 'EAN',
  sku: 'código Marcial Pons',
  isbn: 'ISBN'
};

const EMPTY_CODES: Record<CodeField, string> = { ean: '', sku: '', isbn: '' };

const CODE_FIELDS: CodeField[] = ['ean', 'sku', 'isbn'];

const today = () => new Date().toISOString().split('T')[0];

// Mismo formato que el informe de auditoría de la pestaña de al lado
const formatMoney = (n: number) =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n);

const activeCompanyOrEmpty = () => {
  try {
    return getActiveCompanyId();
  } catch {
    return '';
  }
};

const signed = (n: number) => (n > 0 ? `+${formatQuantity(n)}` : formatQuantity(n));

const diffClass = (n: number) => `module-screen-diff ${n > 0 ? 'positive' : n < 0 ? 'negative' : ''}`;

type LineResult = 'saved' | 'failed';

// --- Piezas comunes -----------------------------------------------------------

const useInventoryCatalogs = () => {
  const [books, setBooks] = useState<Book[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [companies, setCompanies] = useState<Society[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      listBooks().then(list => { if (!cancelled) setBooks(list); }),
      listWarehouses().then(list => { if (!cancelled) setWarehouses(list); }),
      listCompanies().then(list => { if (!cancelled) setCompanies(list); })
    ])
      .catch(err => {
        console.error('Error loading inventory catalogs:', err);
        if (!cancelled) setError(err instanceof Error ? err.message : 'No se pudieron cargar los datos del inventario.');
      })
      .then(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return { books, warehouses, companies, loading, error };
};

// Stock teórico por libro en un almacén: la suma de TODOS sus movimientos, calculada
// por el servidor (stock_levels_list). No se reconstruye aquí desde el listado de
// movimientos porque este va paginado y daría un teórico falso (y ajustes erróneos).
const loadWarehouseStock = async (warehouseId: string): Promise<Record<string, number>> => {
  const stock: Record<string, number> = {};
  (await listWarehouseStock(warehouseId)).forEach(row => { stock[row.bookId] = row.stock; });
  return stock;
};

// Cada diferencia se graba como un ajuste de inventario con su signo (negativo si falta mercancía)
const postDifferences = async (
  warehouseId: string,
  movementDate: string,
  differences: { bookId: string; difference: number }[]
): Promise<Record<string, string | null>> => {
  const results: Record<string, string | null> = {};
  for (const { bookId, difference } of differences) {
    try {
      await createStockMovement({
        warehouseId,
        bookId,
        movementType: 'inventory_adjustment',
        quantity: difference,
        movementDate,
        sourceTable: 'inventory'
      });
      results[bookId] = null;
    } catch (err) {
      console.error('Error posting inventory difference:', err);
      results[bookId] = err instanceof Error ? err.message : 'Error al grabar la diferencia.';
    }
  }
  return results;
};

interface PlaceFieldsProps {
  companies: Society[];
  warehouses: Warehouse[];
  companyId: string;
  warehouseId: string;
  onCompanyChange: (id: string) => void;
  onWarehouseChange: (id: string) => void;
  locked: boolean;
  warehouseLabel: string;
}

// Centro (sociedad) y almacén, como en SAP; el almacén se filtra por la sociedad elegida
const PlaceFields = ({ companies, warehouses, companyId, warehouseId, onCompanyChange, onWarehouseChange, locked, warehouseLabel }: PlaceFieldsProps) => {
  const companyWarehouses = warehouses.filter(w => !companyId || w.societyId === companyId);
  return (
    <>
      <div className="module-form-group">
        <label className="module-form-label">Centro</label>
        <select value={companyId} onChange={(e) => onCompanyChange(e.target.value)} disabled={locked} className="module-form-select">
          <option value="">Todas las sociedades</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div className="module-form-group">
        <label className="module-form-label">{warehouseLabel}</label>
        <select value={warehouseId} onChange={(e) => onWarehouseChange(e.target.value)} disabled={locked} className="module-form-select">
          <option value="">{companyWarehouses.length === 0 ? 'Sin almacenes en esta sociedad' : 'Seleccione...'}</option>
          {companyWarehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
      </div>
    </>
  );
};

interface CodeFieldsProps {
  books: Book[];
  disabled: boolean;
  quantityLabel: string;
  onBook: (book: Book, quantity: number) => void;
  onError: (message: string) => void;
}

// Lectura por código de barras: el lector escribe el código y pulsa Intro
const CodeFields = ({ books, disabled, quantityLabel, onBook, onError }: CodeFieldsProps) => {
  const [codes, setCodes] = useState(EMPTY_CODES);
  const [quantity, setQuantity] = useState('1');

  const search = (field: CodeField) => {
    const value = codes[field].trim();
    if (!value) return;
    const book = findBookByCode(books, value, field === 'sku' ? 'sku' : 'isbn');
    if (!book) {
      onError(`No hay ningún libro con ${CODE_LABELS[field]} ${value}.`);
      return;
    }
    onBook(book, Math.max(1, Math.round(parseNumber(quantity, 1))));
    setCodes(EMPTY_CODES);
    setQuantity('1');
  };

  // El botón hace lo mismo que Intro en el primer código que esté relleno
  const handleAddClick = () => {
    const field = CODE_FIELDS.find(f => codes[f].trim());
    if (field) search(field);
    else onError('Escriba un código EAN, Marcial Pons o ISBN.');
  };

  const codeInput = (field: CodeField, label: string) => (
    <div className="module-form-group">
      <label className="module-form-label">{label}</label>
      <input
        type="text"
        value={codes[field]}
        onChange={(e) => { const val = e.target.value; setCodes(prev => ({ ...prev, [field]: val })); }}
        onKeyDown={(e) => { if (e.key === 'Enter') search(field); }}
        disabled={disabled}
        className="module-form-input"
      />
    </div>
  );

  return (
    <>
    <div className="module-screen-fields">
      {codeInput('ean', 'Código EAN')}
      {codeInput('sku', 'Código Marcial Pons')}
      {codeInput('isbn', 'Código ISBN')}
      <div className="module-form-group">
        <label className="module-form-label">{quantityLabel}</label>
        <input
          type="number"
          min="1"
          step="1"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          disabled={disabled}
          className="module-form-input"
        />
      </div>
    </div>
    <div className="module-screen-actions">
      <button type="button" onClick={handleAddClick} disabled={disabled} className="module-create-btn">
        <Plus size={12} /> Añadir
      </button>
    </div>
    </>
  );
};

const ErrorBox = ({ message }: { message: string }) => (
  message ? (
    <div className="module-form-error">
      <AlertCircle size={14} style={{ flexShrink: 0 }} />
      <span>{message}</span>
    </div>
  ) : null
);

// --- Recuento de inventario ---------------------------------------------------

interface CountLine {
  bookId: string;
  title: string;
  isbn: string;
  sku: string;
  price: number;
  theoretical: number;
  counted: string;
  unread?: boolean; // con stock y sin leer en un inventario completo
  result?: LineResult;
  resultError?: string;
}

interface InventoryScreenProps {
  onSaved: () => void;
}

// Inventario en un solo paso: en SAP son tres (crear documento, entrar recuento y
// eliminar diferencias). Se elige centro y almacén, se leen los libros y al grabar
// cada diferencia entra como ajuste de inventario. En el inventario completo (el
// anual de tienda) los libros con stock que no se han leído pasan a 0, así que el
// stock queda igual a lo contado. La lectura no se guarda hasta que haya endpoint.
export const InventoryScreen = ({ onSaved }: InventoryScreenProps) => {
  const { notify } = useToast();
  const { books, warehouses, companies, loading, error: catalogError } = useInventoryCatalogs();

  const [companyId, setCompanyId] = useState(activeCompanyOrEmpty);
  const [warehouseId, setWarehouseId] = useState('');
  const [countDate, setCountDate] = useState(today);
  const [postingDate, setPostingDate] = useState(today);
  const [reference, setReference] = useState('');
  const [deviationPct, setDeviationPct] = useState('');
  const [threshold, setThreshold] = useState('');
  const [groupingClass, setGroupingClass] = useState('');
  const [isFull, setIsFull] = useState(false);
  const [confirmUnread, setConfirmUnread] = useState<CountLine[] | null>(null);

  const [stock, setStock] = useState<Record<string, number> | null>(null);
  const [stockLoading, setStockLoading] = useState(false);
  const [lines, setLines] = useState<CountLine[]>([]);
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const refreshStock = async (id: string) => {
    setStock(null);
    if (!id) return;
    setStockLoading(true);
    try {
      setStock(await loadWarehouseStock(id));
    } catch (err) {
      console.error('Error loading warehouse stock:', err);
      setError(err instanceof Error ? err.message : 'No se pudo cargar el stock del almacén.');
    } finally {
      setStockLoading(false);
    }
  };

  const handleWarehouseChange = (id: string) => {
    setWarehouseId(id);
    setError('');
    refreshStock(id);
  };

  const handleCompanyChange = (id: string) => {
    setCompanyId(id);
    if (warehouseId && !warehouses.some(w => w.id === warehouseId && (!id || w.societyId === id))) handleWarehouseChange('');
  };

  const handleBook = (book: Book, quantity: number) => {
    setError('');
    setLines(prev => {
      const i = prev.findIndex(l => l.bookId === book.id && !l.result);
      if (i !== -1) {
        const next = [...prev];
        next[i] = { ...next[i], counted: String(parseNumber(next[i].counted) + quantity) };
        return next;
      }
      return [...prev, {
        bookId: book.id,
        title: book.title,
        isbn: book.isbn,
        sku: book.sku,
        price: book.price,
        theoretical: stock?.[book.id] || 0,
        counted: String(quantity)
      }];
    });
  };

  const updateCounted = (bookId: string, counted: string) => {
    setLines(prev => prev.map(l => (l.bookId === bookId && !l.result ? { ...l, counted } : l)));
  };

  const removeLine = (bookId: string) => setLines(prev => prev.filter(l => l.bookId !== bookId || Boolean(l.result)));

  const handleClear = () => {
    setLines([]);
    setReference('');
    setGroupingClass('');
    setError('');
  };

  const differenceOf = (l: CountLine) => parseNumber(l.counted) - l.theoretical;
  const deviationOf = (l: CountLine) => {
    const difference = differenceOf(l);
    if (l.theoretical === 0) return difference === 0 ? 0 : 100;
    return (difference / Math.abs(l.theoretical)) * 100;
  };
  // Las tolerancias del documento de inventario marcan la línea para revisarla; no bloquean nada
  const needsReview = (l: CountLine) => {
    const pct = parseNumber(deviationPct, NaN);
    const limit = parseNumber(threshold, NaN);
    return (!isNaN(pct) && Math.abs(deviationOf(l)) > pct) || (!isNaN(limit) && Math.abs(differenceOf(l) * l.price) > limit);
  };

  const pendingLines = lines.filter(l => !l.result);
  const withDifference = lines.filter(l => differenceOf(l) !== 0);
  const unitsPlus = withDifference.reduce((acc, l) => acc + Math.max(differenceOf(l), 0), 0);
  const unitsMinus = withDifference.reduce((acc, l) => acc + Math.min(differenceOf(l), 0), 0);
  const totalValue = lines.reduce((acc, l) => acc + differenceOf(l) * l.price, 0);

  // Libros con stock en el almacén que no se han leído: en un inventario completo cuentan como 0
  const unreadLines = (): CountLine[] => Object.keys(stock || {})
    .filter(id => (stock || {})[id] !== 0 && !lines.some(l => l.bookId === id))
    .map(id => {
      const book = books.find(b => b.id === id);
      return { bookId: id, title: book?.title || `#${id}`, isbn: book?.isbn || '', sku: book?.sku || '', price: book?.price || 0, theoretical: (stock || {})[id], counted: '0', unread: true };
    });

  const handleSave = () => {
    if (isSaving || pendingLines.length === 0) return;
    if (!warehouseId) { setError('Elija el almacén del inventario.'); return; }
    if (!postingDate) { setError('Indique la fecha de contabilización.'); return; }
    const uncounted = pendingLines.find(l => l.counted.trim() === '');
    if (uncounted) { setError(`Falta el recuento de "${uncounted.title}".`); return; }
    setError('');
    if (isFull) setConfirmUnread(unreadLines());
    else postInventory([]);
  };

  const postInventory = async (unread: CountLine[]) => {
    setConfirmUnread(null);
    setIsSaving(true);
    setLines(prev => [...prev, ...unread]);
    const toPost = [...pendingLines, ...unread].filter(l => differenceOf(l) !== 0);
    const results = await postDifferences(warehouseId, postingDate, toPost.map(l => ({ bookId: l.bookId, difference: differenceOf(l) })));
    setLines(prev => prev.map(l => {
      if (l.result) return l;
      const failure = results[l.bookId];
      return failure ? { ...l, result: 'failed', resultError: failure } : { ...l, result: 'saved' };
    }));

    const failed = Object.values(results).filter(Boolean).length;
    if (failed > 0) {
      notify('Aviso', `Inventario grabado con ${toPost.length - failed} ajuste(s), pero ${failed} diferencia(s) no entraron.`, 'warning');
    } else {
      notify('Éxito', toPost.length > 0 ? `Inventario grabado: ${toPost.length} ajuste(s) de stock.` : 'Inventario grabado sin diferencias.', 'info');
    }
    setIsSaving(false);
    refreshStock(warehouseId);
    onSaved();
  };

  const lineStatus = (l: CountLine) => {
    if (l.result === 'saved') return <span className="module-badge completed">Grabada</span>;
    if (l.result === 'failed') return <span className="module-badge cancelled" title={l.resultError}>Error</span>;
    if (l.unread) return <span className="module-badge overdue">No leído</span>;
    if (differenceOf(l) === 0) return <span className="module-badge active">Sin diferencia</span>;
    if (needsReview(l)) return <span className="module-badge overdue">Revisar</span>;
    return <span className="module-badge pending">Diferencia</span>;
  };

  const locked = lines.length > 0 || isSaving;
  const canScan = !loading && Boolean(warehouseId) && Boolean(stock) && !isSaving;

  return (
    <div className="module-screen">
      <div className="module-screen-actions">
        <button type="button" onClick={handleSave} disabled={isSaving || pendingLines.length === 0} className="module-form-submit-btn">
          <Save size={12} /> {isSaving ? 'Grabando...' : 'Grabar inventario'}
        </button>
        <button type="button" onClick={handleClear} disabled={isSaving} className="module-form-cancel-btn">
          <RotateCcw size={12} /> Limpiar
        </button>
        <button type="button" disabled title="Pendiente de endpoint" className="module-form-cancel-btn">
          <Save size={12} /> Guardar lectura
        </button>
      </div>

      <ErrorBox message={error || catalogError} />

      {loading ? <ScreenSkeleton /> : (
      <div className="module-screen-grid">
        <section className="module-panel module-screen-block">
          <h3 className="module-panel-title"><MapPin size={14} className="module-panel-title-icon" /> Lugar del inventario</h3>
          <div className="module-screen-fields">
            <PlaceFields
              companies={companies}
              warehouses={warehouses}
              companyId={companyId}
              warehouseId={warehouseId}
              onCompanyChange={handleCompanyChange}
              onWarehouseChange={handleWarehouseChange}
              locked={locked}
              warehouseLabel="Almacén"
            />
            <div className="module-form-group">
              <label className="module-form-label">Stock especial</label>
              <input type="text" value="" placeholder="—" disabled title="Pendiente de endpoint" className="module-form-input" />
            </div>
            <div className="module-form-group">
              <label className="module-form-label">Fecha de recuento</label>
              <input type="date" value={countDate} onChange={(e) => setCountDate(e.target.value)} className="module-form-input" />
            </div>
            <div className="module-form-group">
              <label className="module-form-label">Fecha de contabilización</label>
              <input type="date" value={postingDate} onChange={(e) => setPostingDate(e.target.value)} className="module-form-input" />
            </div>
          </div>
          {locked && <p className="module-screen-hint">Para cambiar de almacén, limpie el recuento.</p>}
        </section>

        <section className="module-panel module-screen-block">
          <h3 className="module-panel-title"><ClipboardList size={14} className="module-panel-title-icon" /> Otras especificaciones</h3>
          {/* Mismas opciones que SAP; las tres primeras no tienen todavía nada detrás */}
          <div className="module-screen-checks">
            <label title="Pendiente de endpoint"><input type="checkbox" disabled /> Bloq. contabiliz.</label>
            <label title="Pendiente de endpoint"><input type="checkbox" disabled /> Fijar stock teórico</label>
            <label title="Pendiente de endpoint"><input type="checkbox" disabled /> Lotes con pet. borr.</label>
          </div>
          <div className="module-screen-fields">
            <div className="module-form-group">
              <label className="module-form-label">Número de inventario</label>
              <input type="text" value="" placeholder="Pendiente de endpoint" disabled className="module-form-input" />
            </div>
            <div className="module-form-group">
              <label className="module-form-label">Referencia invent.</label>
              <input type="text" value={reference} onChange={(e) => setReference(e.target.value)} className="module-form-input" />
            </div>
            <div className="module-form-group">
              <label className="module-form-label">Clase agrupación</label>
              <input type="text" value={groupingClass} onChange={(e) => setGroupingClass(e.target.value)} className="module-form-input" />
            </div>
            <div className="module-form-group">
              <label className="module-form-label">Desviación en %</label>
              <input type="number" min="0" step="1" value={deviationPct} onChange={(e) => setDeviationPct(e.target.value)} className="module-form-input" />
            </div>
            <div className="module-form-group">
              <label className="module-form-label">Valor umbral (€)</label>
              <input type="number" min="0" step="0.01" value={threshold} onChange={(e) => setThreshold(e.target.value)} className="module-form-input" />
            </div>
          </div>
          <div className="module-screen-checks">
            <label>
              <input type="checkbox" checked={isFull} onChange={(e) => setIsFull(e.target.checked)} disabled={isSaving} />
              Inventario completo del almacén
            </label>
          </div>
          <p className="module-screen-hint">
            {isFull
              ? 'Inventario completo (el anual de tienda): al grabar, los libros con stock que no se hayan leído pasan a 0.'
              : 'Las líneas que superen la desviación o el valor umbral se marcan para revisar.'}
          </p>
        </section>

        <section className="module-panel module-screen-block">
          <h3 className="module-panel-title"><ScanLine size={14} className="module-panel-title-icon" /> Código libro</h3>
          <CodeFields books={books} disabled={!canScan} quantityLabel="Cantidad contada" onBook={handleBook} onError={setError} />
          <p className="module-screen-hint">
            {!warehouseId ? 'Elija el almacén para empezar.'
              : stockLoading ? 'Cargando el stock del almacén...'
              : 'Escanee o escriba el código y pulse Intro o Añadir. Cada lectura suma la cantidad contada.'}
          </p>
        </section>

        <section className="module-panel module-screen-block">
          <h3 className="module-panel-title"><Sigma size={14} className="module-panel-title-icon" /> Resumen</h3>
          <dl className="module-screen-data">
            <dt>Libros</dt><dd>{lines.length}</dd>
            <dt>Con diferencia</dt><dd>{withDifference.length}</dd>
            <dt>Unidades</dt><dd><span className={diffClass(unitsPlus)}>{signed(unitsPlus)}</span> / <span className={diffClass(unitsMinus)}>{signed(unitsMinus)}</span></dd>
            <dt>Valor diferencias (PVP)</dt><dd><span className={diffClass(totalValue)}>{formatMoney(totalValue)}</span></dd>
          </dl>
        </section>
      </div>
      )}

      <section className="module-panel module-screen-block">
        <h3 className="module-panel-title"><ClipboardList size={14} className="module-panel-title-icon" /> Líneas del inventario</h3>
        <div className="purchases-view__lines-wrapper">
          <table className="purchases-view__lines-table purchases-view__lines-table--readonly module-screen-table">
            <thead>
              <tr>
                <th>St</th>
                <th>Código MP</th>
                <th>Título del libro</th>
                <th className="right">Stock teórico</th>
                <th className="right">Recuento</th>
                <th className="right">Diferencia</th>
                <th className="right">Desv. %</th>
                <th className="right">PVP</th>
                <th className="right">Valor dif.</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 ? (
                <tr><td colSpan={10} className="purchases-view__lines-empty">Lea un libro para empezar el recuento.</td></tr>
              ) : lines.map(l => {
                const difference = differenceOf(l);
                const done = Boolean(l.result);
                return (
                  <tr key={l.bookId} className={l.result === 'saved' ? 'purchases-view__line-done' : ''}>
                    <td>{lineStatus(l)}</td>
                    <td>{l.sku || '—'}</td>
                    <td>
                      <div className="purchases-view__line-desc">{l.title}</div>
                      {l.isbn && <div className="purchases-view__line-sub">{l.isbn}</div>}
                    </td>
                    <td className="right">{formatQuantity(l.theoretical)}</td>
                    <td className="right">
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={l.counted}
                        onChange={(e) => updateCounted(l.bookId, e.target.value)}
                        disabled={done || isSaving}
                        className="module-form-input right"
                      />
                    </td>
                    <td className="right"><span className={diffClass(difference)}>{signed(difference)}</span></td>
                    <td className="right">{signed(Math.round(deviationOf(l)))}%</td>
                    <td className="right">{formatMoney(l.price)}</td>
                    <td className="right"><span className={diffClass(difference)}>{formatMoney(difference * l.price)}</span></td>
                    <td>
                      {!done && (
                        <button type="button" onClick={() => removeLine(l.bookId)} disabled={isSaving} className="module-screen-remove" title="Quitar línea">
                          <X size={12} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <Modal
        isOpen={confirmUnread !== null}
        type="warning"
        title="Grabar inventario completo"
        message={`El stock del almacén quedará igual a lo contado. ${confirmUnread?.length || 0} libro(s) con stock que no se han leído pasan a 0.`}
        confirmText="Grabar"
        cancelText="Cancelar"
        onConfirm={() => postInventory(confirmUnread || [])}
        onCancel={() => setConfirmUnread(null)}
      />
    </div>
  );
};
